/**
 * Module Client #05 — Mes finances
 * Source : Guide_Client_Baobab_Connect.pdf · Chapitre 3.4 (Recevoir et régler une facture)
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/client/${name}`;

const moduleClientFinances = {
  id: 'client-mes-finances',
  population: 'client',
  order: 5,
  category: 'Finance',
  title: 'Mes finances',
  subtitle: 'Maîtriser factures, paiements, relevés et signalements',
  icon: 'DollarSign',
  duration: '25 min',
  level: 'Intermédiaire',
  audience: 'Comptabilité & gestionnaires',
  pdfUrl: '/api/uploads/docs/training/client-mes-finances.pdf',
  slides: [
    { type: 'cover', eyebrow: 'Module 05 — Client', title: 'Mes finances', subtitle: 'Recevoir, suivre et régler vos factures en toute conformité', icon: 'DollarSign', duration: '25 min', audience: 'Comptabilité', level: 'Intermédiaire' },
    { type: 'intro', title: 'Objectifs', intro: 'Le module Finances centralise toutes vos transactions avec Baobab Logistics. À la fin de ce module, vous saurez exécuter le cycle financier complet.', objectives: [
      'Consulter, télécharger et imprimer une facture (PDF conforme OHADA)',
      'Comprendre les statuts de paiement (À payer, En retard, Payée, Litige)',
      'Signaler un paiement effectué (par virement, mobile money, espèces)',
      'Demander un avoir en cas d\'erreur de facturation',
      'Exporter votre relevé pour intégration comptable (CSV, Sage, Odoo)',
      'Configurer des alertes d\'échéance pour anticiper les paiements',
    ]},
    { type: 'concept', eyebrow: 'Vocabulaire OHADA', icon: 'BookOpen', title: 'Les statuts de facture',
      lede: 'Chaque facture passe par un cycle de vie balisé :',
      bullets: [
        '📄 Émise : facture générée, en attente de validation interne Baobab',
        '✉️ Envoyée : facture transmise au client, en attente de paiement',
        '⏳ Échéance proche : ≤ 7j avant la date limite (alerte automatique)',
        '🔴 En retard : date d\'échéance dépassée',
        '✅ Payée : règlement confirmé et rapproché',
        '🟡 En litige : contestation en cours, suspension du recouvrement',
      ],
      note: 'Le format de numérotation FACT-AAAA-MM-NNN garantit la conformité OHADA et la traçabilité.',
    },
    { type: 'screenshot', eyebrow: 'Vue d\'ensemble', title: 'La page « Mes finances »',
      image: IMG('img_007_07_invoices.png'),
      callouts: [
        { label: 'Synthèse en haut', text: 'Total à payer / En retard / Payé sur la période.', x: 25, y: 12 },
        { label: 'Filtres rapides', text: 'Par statut, période, montant — vues sauvegardées disponibles.', x: 65, y: 22 },
        { label: 'Tableau factures', text: 'Tri par échéance ou par montant ; clic pour ouvrir la facture.', x: 50, y: 50 },
        { label: 'Actions par ligne', text: 'Télécharger PDF, signaler paiement, demander un avoir.', x: 85, y: 55 },
      ],
    },
    { type: 'steps', title: 'Télécharger une facture en 3 clics', steps: [
      { title: 'Ouvrir « Mes finances »', detail: 'Menu de gauche ou KPI « Coût total » du dashboard.' },
      { title: 'Trouver la facture', detail: 'Recherche par numéro ou par opération associée.' },
      { title: 'Cliquer sur l\'icône téléchargement', detail: 'Le PDF officiel s\'ouvre directement.' },
    ]},
    { type: 'screenshot', eyebrow: 'Action critique', title: 'Signaler un paiement effectué',
      image: IMG('img_106_signaler_paiement.png'),
      callouts: [
        { label: 'Bouton « Signaler paiement »', text: 'Disponible sur chaque facture envoyée ou en retard.', x: 30, y: 25 },
        { label: 'Montant & date', text: 'Saisissez le montant exact et la date du virement.', x: 50, y: 50 },
        { label: 'Référence', text: 'Numéro de transaction (virement bancaire, mobile money...).', x: 50, y: 65 },
        { label: 'Justificatif', text: 'Photo / PDF de la preuve de paiement — facilite le rapprochement.', x: 50, y: 80 },
      ],
    },
    { type: 'concept', eyebrow: 'Modes de paiement', icon: 'DollarSign', title: 'Comment payer Baobab Logistics',
      lede: 'Plusieurs canaux acceptés selon votre contrat :',
      bullets: [
        '🏦 Virement bancaire : RIB indiqué sur chaque facture (recommandé > 500k FCFA)',
        '📱 Mobile money : Orange Money / MTN / Moov (limite quotidienne 500k)',
        '💵 Espèces : uniquement bureau Abidjan, reçu papier obligatoire',
        '💳 Carte bancaire / Wave : à venir Q3 2026',
      ],
      note: 'Toujours signaler le paiement sur la plateforme même pour les espèces — accélère le rapprochement de 5j.',
    },
    { type: 'steps', title: 'Demander un avoir (correction facture)', steps: [
      { title: 'Identifier l\'erreur', detail: 'Mauvais montant, double facturation, marchandise non livrée...' },
      { title: 'Ouvrir la facture concernée', detail: 'Menu actions → « Demander un avoir ».' },
      { title: 'Décrire le motif', detail: 'Texte libre + pièces jointes (BL, photos).' },
      { title: 'Soumettre', detail: 'Votre opérateur Baobab traite sous 48 h ouvrées.' },
      { title: 'Recevoir l\'avoir', detail: 'Notifications email + apparition dans la liste avec statut « Avoir émis ».' },
    ]},
    { type: 'screenshot', eyebrow: 'Export comptable', title: 'Exporter votre relevé pour Sage / Odoo',
      callouts: [
        { label: 'Bouton « Exporter »', text: 'En haut de la liste, propose CSV / Excel / format Sage.', x: 80, y: 15 },
        { label: 'Période', text: 'Sélectionnez la période à exporter (cohérence avec votre clôture mensuelle).', x: 40, y: 35 },
        { label: 'Colonnes incluses', text: 'Date, numéro, client, HT, TVA, TTC, statut, échéance, paiement.', x: 50, y: 60 },
      ],
    },
    { type: 'checklist', title: 'Checklist mensuelle finances', items: [
      'Télécharger les factures du mois échu',
      'Vérifier les statuts de paiement (aucune oubliée)',
      'Signaler les paiements non encore enregistrés',
      'Demander les avoirs nécessaires avant clôture',
      'Exporter le relevé CSV pour votre comptable',
      'Configurer les rappels d\'échéance pour le mois suivant',
    ]},
    { type: 'comparison', title: 'Bonnes pratiques vs erreurs courantes', do: [
      'Signaler immédiatement chaque paiement effectué (pas en différé)',
      'Joindre toujours le justificatif (photo virement, reçu mobile money)',
      'Activer les alertes « Échéance proche » pour ne rien oublier',
      'Exporter le relevé mensuel pour la compta interne',
      'Demander un avoir plutôt que d\'ignorer une erreur',
    ], dont: [
      'Ne payez pas une facture en litige avant résolution',
      'Ne contournez pas la plateforme via virement direct sans signalement',
      'N\'attendez pas la relance pour signaler un paiement effectué',
      'Ne supprimez pas les justificatifs (conservation 10 ans OHADA)',
      'Ne contestez pas une facture par WhatsApp (utilisez « Demander un avoir »)',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Vous avez payé une facture par virement il y a 2 jours. Que faut-il faire ?',
      options: [
        'Attendre que Baobab le voit sur leur compte',
        'Envoyer un email à votre opérateur',
        'Cliquer sur « Signaler paiement » sur la facture + joindre la preuve de virement',
        'Appeler le service comptable de Baobab',
      ],
      answerIndex: 2,
      explanation: 'Le signalement déclenche le rapprochement automatique en interne. Joindre la preuve accélère la validation à < 24 h.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'La facture FACT-2026-05-042 est en statut « En litige ». Que se passe-t-il ?',
      options: [
        'Le recouvrement est suspendu et un agent traite votre contestation',
        'Vous devez payer quand même',
        'Vous serez bloqué pour les futures opérations',
        'La facture est automatiquement annulée',
      ],
      answerIndex: 0,
      explanation: 'Le statut Litige gèle les pénalités et déclenche une revue par votre opérateur. Vous restez libre de créer d\'autres opérations en parallèle.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'Signaler chaque paiement — pas d\'exception, même en espèces.',
      'Le PDF de facture est conforme OHADA, opposable juridiquement.',
      'L\'avoir corrige une erreur ; ne payez jamais une facture erronée.',
      'L\'export CSV/Sage automatise votre clôture mensuelle.',
      'Les alertes d\'échéance évitent 90 % des retards.',
    ]},
    { type: 'cta', title: 'Module 05 terminé !', subtitle: 'Vous maîtrisez le cycle financier complet.',
      actions: [
        { label: 'Ouvrir mes finances', href: '/finances' },
        { label: 'Module suivant', href: '/docs/client/training' },
      ],
    },
  ],
};

export default moduleClientFinances;
